import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { TITLES, COUNTRIES, DIAL_CODES, isValidEmail } from "../lib/formData";
import { MessageCircle, X, Send, Loader2, Check } from "lucide-react";

const WHATSAPP_HANDLE = "nowirokert"; // WhatsApp Business handle @nowirokert (display)
// wa.me requires an E.164 phone number (digits only, no '+'). Set this to your
// WhatsApp Business number to make the link resolve to a real chat.
const WHATSAPP_NUMBER = "94713241274"; // WhatsApp Business number (E.164, digits only)
const WHATSAPP_TEXT = "Hello Rokert Retreats — I'd like to enquire about a private retreat.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER || WHATSAPP_HANDLE}?text=${encodeURIComponent(WHATSAPP_TEXT)}`;

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: "Mr", name: "", email: "", country: "", dial: "+44", phone: "", message: "" });
  const [emailErr, setEmailErr] = useState("");

  useEffect(() => {
    const handler = () => { setOpen(true); setSent(false); };
    window.addEventListener("open-concierge", handler);
    return () => window.removeEventListener("open-concierge", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Please add your name and message"); return; }
    if (!isValidEmail(form.email)) { setEmailErr("Please enter a valid email address"); return; }
    setEmailErr("");
    setSending(true);
    try {
      await api.post("/chat", {
        name: `${form.title} ${form.name}`.trim(),
        email: form.email,
        country: form.country,
        phone: form.phone ? `${form.dial} ${form.phone}` : "",
        message: form.message,
      });
      setSent(true);
      setForm({ title: "Mr", name: "", email: "", country: "", dial: "+44", phone: "", message: "" });
    } catch { toast.error("Could not send — please try again"); }
    finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="chat-launcher"
        className={`fixed right-5 bottom-24 md:bottom-6 z-[56] flex items-center gap-3 pl-3 pr-5 py-3 rounded-full bg-rr-gold text-rr-bg shadow-[0_14px_40px_rgba(201,164,107,0.35)] transition-all hover:bg-rr-goldSoft ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
        <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-rr-bg/10">
          <MessageCircle className="w-4.5 h-4.5" strokeWidth={1.6} />
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-rr-gold" />
        </span>
        <span className="text-xs font-semibold tracking-wide uppercase">Concierge</span>
      </button>

      {open && (
        <div className="fixed right-5 bottom-24 md:bottom-6 z-[57] w-[calc(100vw-2.5rem)] sm:w-[380px] rounded-2xl overflow-hidden bg-rr-surface border border-rr-gold/25 shadow-[0_30px_80px_rgba(0,0,0,0.6)] fade-up" data-testid="chat-panel">
          <div className="bg-rr-forest px-5 py-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-11 h-11 rounded-full bg-rr-gold/20 flex items-center justify-center overflow-hidden">
                <img src="/media/rokert-emblem.png" alt="Rokert" className="w-8 h-8 object-contain" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 ring-2 ring-rr-forest" />
              </div>
              <div>
                <p className="font-heading text-xl text-rr-ivory leading-none">Rokert Concierge</p>
                <p className="text-[11px] text-emerald-300/90 mt-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> Fast replies guaranteed
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-rr-ivory/60 hover:text-rr-ivory"><X className="w-5 h-5" /></button>
          </div>

          <div className="p-5">
            {sent ? (
              <div className="text-center py-6" data-testid="chat-success">
                <div className="w-14 h-14 rounded-full bg-rr-gold/15 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-rr-gold" strokeWidth={1.5} /></div>
                <p className="font-heading text-2xl text-rr-ivory mt-4">Message received</p>
                <p className="text-sm text-rr-muted mt-2">Our concierge will reply to your email shortly. Thank you for reaching out.</p>
                <button onClick={() => setSent(false)} className="btn-outline mt-5 px-6 py-2.5 text-xs uppercase">Send another</button>
              </div>
            ) : (
              <>
                {/* WhatsApp — instant message */}
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="chat-whatsapp"
                  className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-lg font-semibold text-sm text-white mb-4"
                  style={{ background: "#25D366" }}>
                  <WhatsAppIcon className="w-5 h-5" /> Message us on WhatsApp
                </a>
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex-1 h-px bg-rr-line" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-rr-muted">or email us</span>
                  <span className="flex-1 h-px bg-rr-line" />
                </div>
                <form onSubmit={submit} noValidate className="space-y-3">
                  <div className="grid grid-cols-[80px_1fr] gap-3">
                    <select value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="chat-title"
                      className="w-full bg-rr-bg border border-rr-line rounded-lg px-3 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none">
                      {TITLES.map((tt) => <option key={tt} value={tt} className="bg-rr-surface">{tt}</option>)}
                    </select>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="chat-name"
                      className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory placeholder:text-rr-muted focus:border-rr-gold outline-none" />
                  </div>
                  <div>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" data-testid="chat-email"
                      className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory placeholder:text-rr-muted focus:border-rr-gold outline-none" />
                    {emailErr && <p data-testid="chat-email-error" className="text-red-400 text-xs mt-1.5">{emailErr}</p>}
                  </div>
                  <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="chat-country"
                    className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none">
                    <option value="" className="bg-rr-surface text-rr-muted">Country</option>
                    {COUNTRIES.map((c) => <option key={c.n} value={c.n} className="bg-rr-surface">{c.n}</option>)}
                  </select>
                  <div className="grid grid-cols-[100px_1fr] gap-3">
                    <select value={form.dial} onChange={(e) => setForm({ ...form, dial: e.target.value })} data-testid="chat-dial"
                      className="w-full bg-rr-bg border border-rr-line rounded-lg px-3 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none">
                      {DIAL_CODES.map((d) => <option key={d} value={d} className="bg-rr-surface">{d}</option>)}
                    </select>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="chat-phone"
                      className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory placeholder:text-rr-muted focus:border-rr-gold outline-none" />
                  </div>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How may we help?" rows={3} data-testid="chat-message"
                    className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory placeholder:text-rr-muted focus:border-rr-gold outline-none resize-none" />
                  <button type="submit" disabled={sending} data-testid="chat-send" className="btn-gold w-full py-3.5 flex items-center justify-center gap-2 text-xs uppercase">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <Send className="w-4 h-4" strokeWidth={1.6} /></>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
